/**
 * CameraG100SettingsSheet (#403) — the camera's owner changes a G100 camera's settings from the
 * Cameras page, in plain words and in five groups, through the owner API that already exists:
 * GET /api/cameras/{id}/g100/ (the set the server holds, its version, whether the camera has taken
 * it, whether the caller may change it) and PATCH /api/cameras/{id}/g100/settings/ with only the
 * changed fields. The controls are the Test Console's (G100SettingControls); the three advanced
 * settings never appear here and the three the camera cannot change are shown as text. A user the
 * camera is shared with sees the values read-only.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, SlidersHorizontal, Clock, CheckCircle2, Lock, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { g100OwnerApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { G100_OWNER_KEYS } from '@/lib/sharePermissions'
import {
  G100_CUSTOMER_GROUPS, G100_CUSTOMER_LAYOUT, G100_FIXED_FOR_CUSTOMERS, G100_SPEC_BY_NAME,
  type G100ChoiceName, type G100SettingName,
} from '@/lib/g100Settings'
import { Control, fieldErrors, invalid, reason, same, statusOf, when } from '@/components/g100/G100SettingControls'
import type { Camera, G100OwnerStatus } from '@/types'

interface CameraG100SettingsSheetProps {
  camera: Camera
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Values = Record<string, unknown>
const FIXED = new Set<string>(G100_FIXED_FOR_CUSTOMERS)

export function CameraG100SettingsSheet({ camera, open, onOpenChange }: CameraG100SettingsSheetProps) {
  const { t } = useTranslation('cameras')
  const queryClient = useQueryClient()
  const status = useQuery({
    queryKey: queryKeys.g100Owner.status(camera.id),
    queryFn: () => g100OwnerApi.status(camera.id),
    enabled: open,
    refetchInterval: (query) => (query.state.data?.settings.pending ? queryOptions.g100Owner.statusWhileWaitingMs : false),
  })
  const data = status.data
  const values: Values = useMemo(() => data?.settings.values ?? {}, [data])
  const canControl = data ? data.can_control : camera.is_owner
  // #410 — a settings tick edits the settings key by key; the eight owner-only keys stay with the owner or a superuser.
  const canChange = canControl || data?.can_change_settings === true
  const keyLocked = (name: string) => !canControl && (!canChange || G100_OWNER_KEYS.has(name))

  const [draft, setDraft] = useState<Values>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  useEffect(() => { if (open) { setDraft({}); setErrors({}) } }, [open, camera.id])
  const current = (name: string) => (name in draft ? draft[name] : values[name])
  const set = (name: string, value: unknown) => {
    setDraft((d) => {
      const next = { ...d }
      if (same(value, values[name])) delete next[name]
      else next[name] = value
      return next
    })
    setErrors((e) => { const next = { ...e }; delete next[name]; return next })
  }
  const changes = useMemo(
    () => Object.fromEntries(Object.entries(draft).filter(([k, v]) => !same(v, values[k]))), [draft, values])
  const changedCount = Object.keys(changes).length
  const blocked = Object.keys(changes).some((name) => {
    const spec = G100_SPEC_BY_NAME[name as G100SettingName]
    return spec !== undefined && invalid(spec, changes[name])
  })

  // The customer's words for a choice: one object per choice setting in the cameras locale
  // (g100Settings.choices.<name>), read as the console reads its own (typed keys, no template literal).
  const choiceLabel = (name: G100ChoiceName, c: number): string => {
    const labels = t(`g100Settings.choices.${name}`, { returnObjects: true }) as unknown
    return (labels && typeof labels === 'object' ? (labels as Record<string, string>)[String(c)] : undefined) ?? String(c)
  }

  const save = useMutation({
    mutationFn: () => g100OwnerApi.changeSettings(camera.id, changes),
    onSuccess: (saved) => {
      toast.success(t('g100Settings.saved', { version: saved.version }))
      setDraft({})
      setErrors({})
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Owner.status(camera.id) })
    },
    onError: (error: unknown) => {
      if (statusOf(error) === 429) {
        toast.error(t('g100Settings.saveFailed'), t('g100Settings.throttled'))
        return
      }
      setErrors(fieldErrors(error))
      toast.error(t('g100Settings.saveFailed'), reason(error))
    },
  })
  const isSaving = save.isPending

  return (
    <Dialog.Root open={open} onOpenChange={isSaving ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-full max-w-2xl border-l border-border bg-background shadow-elevation-3 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <Dialog.Title className="text-base font-semibold truncate">{t('g100Settings.title')}</Dialog.Title>
                <p className="text-xs text-muted-foreground truncate">{camera.name} · {camera.imei}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {data && <StateChip pending={data.settings.pending} source={data.settings.source} />}
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" disabled={isSaving} aria-label={t('g100Settings.close')}>
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>
          </div>

          {data && (
            <div className="px-5 py-3 bg-muted/40 border-b border-border shrink-0 text-xs text-muted-foreground flex items-start gap-2">
              {data.settings.pending ? <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                : untouched(data.settings.source) ? <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                : <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
              <p className="space-x-1">
                {data.settings.pending ? (
                  <>
                    <span className="font-medium text-foreground">{t('g100Settings.waiting')}</span>
                    <span>{t('g100Settings.waitingHint')}</span>
                  </>
                ) : data.settings.source === 'report' ? (
                  // #405 — nothing of ours yet: the values are the camera's own last report
                  <>
                    <span className="font-medium text-foreground">{t('g100Settings.fromReport')}</span>
                    {data.settings.reported_at && <span>{t('g100Settings.appliedAt', { when: when(data.settings.reported_at) })}</span>}
                  </>
                ) : data.settings.source === 'defaults' ? (
                  // #405 — nothing of ours and no complete report: the documented defaults, sent at the camera's next connection
                  <span className="font-medium text-foreground">{t('g100Settings.fromDefaults')}</span>
                ) : (
                  <>
                    <span className="font-medium text-foreground">{t('g100Settings.applied')}</span>
                    {data.settings.reported_at && <span>{t('g100Settings.appliedAt', { when: when(data.settings.reported_at) })}</span>}
                  </>
                )}
              </p>
            </div>
          )}
          {data && !canControl && (
            <div className="px-5 py-3 border-b border-border shrink-0 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
              <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              {canChange ? <p>{t('g100Settings.ownerKeysLocked')}</p> : (
                <div className="space-y-1">
                  <p>{t('g100Settings.ownerOnly')}</p>
                  <p>{t('g100Settings.askOwner')}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
            {status.isLoading ? (
              <p className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 mr-2 animate-spin" />…</p>
            ) : status.isError || !data ? (
              <p className="text-sm text-destructive">{t('g100Settings.loadError')}</p>
            ) : (
              G100_CUSTOMER_GROUPS.map((group) => (
                <section key={group} aria-label={t(`g100Settings.groups.${group}`)}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t(`g100Settings.groups.${group}`)}</h3>
                  <ul className="divide-y divide-border/40">
                    {G100_CUSTOMER_LAYOUT[group].map((name) => {
                      const spec = G100_SPEC_BY_NAME[name]
                      const wide = spec.kind === 'windows' || spec.kind === 'species' || spec.kind === 'zone'
                      const changed = name in changes
                      return (
                        <li key={name} data-testid="g100-customer-row" data-changed={changed ? 'true' : undefined}
                          className={cn('-mx-2 rounded px-2 py-2 border-l-2 border-transparent transition-colors',
                            changed && 'bg-amber-500/10 border-amber-500')}>
                          <div className={cn('grid gap-x-3 gap-y-1', wide ? 'grid-cols-1' : 'grid-cols-[minmax(0,1fr)_auto] items-center')}>
                            <div className="min-w-0">
                              <p className="text-sm">{t(`g100Settings.fields.${name}`)}</p>
                              {errors[name] && <p className="text-xs text-destructive">{errors[name]}</p>}
                            </div>
                            <div className={cn(!wide && 'justify-self-end')}>
                              {FIXED.has(name) && spec.kind === 'choice' ? (
                                <span className="text-xs text-muted-foreground" title={t('g100Settings.fixed')}>
                                  {choiceLabel(spec.name, spec.choices[0])}
                                </span>
                              ) : (
                                <Control spec={spec} value={current(name)} disabled={keyLocked(name)}
                                  choiceLabel={spec.kind === 'choice' ? (c) => choiceLabel(spec.name, c) : undefined}
                                  onChange={(v) => set(name, v)} />
                              )}
                            </div>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              ))
            )}
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
            {changedCount > 0 && canChange && (
              <span className="mr-auto text-xs text-muted-foreground">{t('g100Settings.changed', { count: changedCount })}</span>
            )}
            <Dialog.Close asChild>
              <Button variant="outline" disabled={isSaving}>{t('g100Settings.close')}</Button>
            </Dialog.Close>
            {canChange && data && (
              <Button onClick={() => save.mutate()} disabled={changedCount === 0 || blocked || isSaving}>
                {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {t('g100Settings.save')}
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

type SettingsSource = G100OwnerStatus['settings']['source']

// #405 — a set nobody has changed from here yet (the camera's own report, or the documented defaults).
// A missing source (an older API) reads as ours: "Applied", which is what the sheet showed until #405.
function untouched(source: SettingsSource): boolean {
  return source === 'report' || source === 'defaults'
}

function StateChip({ pending, source }: { pending: boolean; source: SettingsSource }) {
  const { t } = useTranslation('cameras')
  if (pending) return (
    <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/30">
      {t('g100Settings.chipWaiting')}
    </span>
  )
  if (untouched(source)) return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground border border-border">
      {t('g100Settings.chipNotChanged')}
    </span>
  )
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
      {t('g100Settings.chipApplied')}
    </span>
  )
}
